import React, { useEffect, useState, useRef } from "react";
import "../Styling/balancesheet.css";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const TrialBalanceReport = () => {
  const { user } = useAuth();
  const tableRef = useRef(null);
  
  const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
  const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    organisationId: user?.organisationId || "",
    companyCode: "",
    fiscalYear: new Date().getFullYear(),
    asOnDate: todayStr
  });

  const [companyCodes, setCompanyCodes] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);

  // ===============================
  // FETCH COMPANY CODES
  // ===============================
  useEffect(() => {
    if (!user?.organisationId || !token) return;

    const fetchCompanyCodes = async () => {
      try {
        const res = await axios.get(
          `${Base_URL}/Balancesheet/bs-company-codes-with-names/${user.organisationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setCompanyCodes(res.data || []);

        if (res.data && res.data.length > 0) {
          const firstCompany = res.data[0];
          setFilters(prev => ({
            ...prev,
            companyCode: firstCompany.companyCode
          }));
        }
      } catch (err) {
        console.error("Error fetching company codes", err);
      }
    };

    fetchCompanyCodes();
  }, [user?.organisationId, token]);

  // ===============================
  // FISCAL YEARS
  // ===============================
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setFiscalYears(Array.from({ length: 6 }, (_, i) => currentYear - i));
  }, []);

  // ===============================
  // FETCH TRIAL BALANCE
  // ===============================
  useEffect(() => {
    if (!filters.companyCode || !user?.organisationId || !token) return;

    const fetchTrialBalance = async () => {
      try {
        setLoading(true);

        let url = `${Base_URL}/Balancesheet/trial-balance/${user.organisationId}/${filters.companyCode}/${filters.fiscalYear}/${filters.asOnDate}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Trial Balance API Response:", res.data); // Debug log
        
        setData(res.data);
      } catch (err) {
        console.error("Error fetching trial balance", err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTrialBalance();
  }, [filters, user?.organisationId, token]);

  // ===============================
  // HANDLE FILTER CHANGE
  // ===============================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // ===============================
  // GET COMPANY INFO FROM DATA
  // ===============================
  const getCompanyName = () => {
    if (!data) return filters.companyCode;
    
    // Try different possible fields from API response
    return data.companyName || 
           data.companyInfo?.companyName || 
           `Company: ${data.companyCode || filters.companyCode}`;
  };

  const getCompanyAddress = () => {
    if (!data) return "";
    
    return data.companyAddress || 
           data.companyInfo?.companyAddress || 
           "";
  };

  const getLogoUrl = () => {
    if (!data) return "";
    
    return data.logoUrl || 
           data.companyInfo?.logoUrl || 
           "";
  };

  // ===============================
  // GENERATE PDF
  // ===============================
  const generatePDF = () => {
    if (!data) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company Name and Address (just like Balance Sheet)
    const companyName = getCompanyName();
    const companyAddress = getCompanyAddress();
    
    // Header
    doc.setFontSize(16);
    doc.setTextColor(26, 61, 124);
    doc.text(companyName, pageWidth / 2, 15, { align: "center" });
    
    if (companyAddress) {
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(companyAddress, pageWidth / 2, 22, { align: "center" });
    }
    
    // Report Title
    doc.setFontSize(14);
    doc.setTextColor(26, 61, 124);
    
    const reportTitle = `Trial Balance - As on ${new Date(filters.asOnDate).toLocaleDateString('en-IN')}`;
    doc.text(reportTitle, pageWidth / 2, companyAddress ? 32 : 25, { align: "center" });
    
    // Report Details
    doc.setFontSize(10);
    doc.setTextColor(80);
    const detailsY = companyAddress ? 39 : 32;
    doc.text(`Financial Year: ${filters.fiscalYear}`, pageWidth / 2, detailsY, { align: "center" });
    doc.text(`Company Code: ${data.companyCode || filters.companyCode}`, pageWidth / 2, detailsY + 7, { align: "center" });
    
    // Prepare table data
    const tableData = data.trialBalanceSummary.rows.map(row => [
      row.glCode || '',
      row.glDescription || '',
      row.debit > 0 ? row.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-',
      row.credit > 0 ? row.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'
    ]);
    
    // Add table
    const startY = detailsY + 17;
    doc.autoTable({
      startY: startY,
      head: [['GL Code', 'GL Description', 'Debit (₹)', 'Credit (₹)']],
      body: tableData,
      headStyles: {
        fillColor: [26, 61, 124],
        textColor: 255,
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
      },
      margin: { horizontal: 10 }
    });
    
    // Add totals
    const finalY = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    
    doc.text(`Total Debit: ₹${data.trialBalanceSummary.totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, 20, finalY);
    doc.text(`Total Credit: ₹${data.trialBalanceSummary.totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, pageWidth - 20, finalY, { align: "right" });
    
    // Status
    doc.text(`Status: ${data.trialBalanceSummary.isBalanced ? "✓ Balanced" : "✗ Not Balanced"}`, pageWidth / 2, finalY + 10, { align: "center" });
    
    // Footer
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: "center" });
    
    // Save PDF
    doc.save(`trial-balance-${data.companyCode || filters.companyCode}-${filters.asOnDate}.pdf`);
  };

  // ===============================
  // GENERATE EXCEL
  // ===============================
  const generateExcel = () => {
    if (!data) return;
    
    const companyName = getCompanyName();
    const companyAddress = getCompanyAddress();
    
    // Prepare worksheet data
    const worksheetData = [
      // Header
      [companyName],
      [companyAddress || ""],
      [],
      ["TRIAL BALANCE"],
      [`As On Date: ${new Date(filters.asOnDate).toLocaleDateString('en-IN')}`],
      [`Financial Year: ${filters.fiscalYear}`],
      [`Company Code: ${data.companyCode || filters.companyCode}`],
      [],
      // Table headers
      ["GL Code", "GL Description", "Debit (₹)", "Credit (₹)"],
      // Table rows
      ...data.trialBalanceSummary.rows.map(row => [
        row.glCode,
        row.glDescription,
        row.debit > 0 ? row.debit : 0,
        row.credit > 0 ? row.credit : 0
      ]),
      [],
      // Totals
      ["TOTAL DEBIT", "", data.trialBalanceSummary.totalDebit, ""],
      ["TOTAL CREDIT", "", "", data.trialBalanceSummary.totalCredit],
      [],
      // Status
      ["STATUS", data.trialBalanceSummary.isBalanced ? "BALANCED" : "NOT BALANCED"],
      [],
      // Footer
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [`Organisation ID: ${user?.organisationId}`]
    ];
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // GL Code
      { wch: 50 }, // Description
      { wch: 20 }, // Debit
      { wch: 20 }  // Credit
    ];
    
    // Merge cells for headers
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }); // Company name
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 3 } }); // Company address
    ws['!merges'].push({ s: { r: 3, c: 0 }, e: { r: 3, c: 3 } }); // Title
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");
    
    // Save file
    XLSX.writeFile(wb, `trial-balance-${data.companyCode || filters.companyCode}-${filters.asOnDate}.xlsx`);
  };

  // ===============================
  // HANDLE LOGO ERROR
  // ===============================
  const handleLogoError = (e) => {
    e.target.style.display = 'none';
  };

  // ===============================
  // UI
  // ===============================
  return (
    <div className="bsr-wrapper">
      {/* Filters */}
      <div className="bsr-filters">
        <h3>Trial Balance Filters</h3>
        <div className="bsr-filter-grid">
          <div className="bsr-filter-group">
            <label>Organisation</label>
            <div className="bsr-fixed-field">{user?.organisationId}</div>
          </div>

          <div className="bsr-filter-group">
            <label>Company Code</label>
            <select
              name="companyCode"
              value={filters.companyCode}
              onChange={handleFilterChange}
            >
              {companyCodes.map(c => (
                <option key={c.companyCode} value={c.companyCode}>
                  {c.companyCode} - {c.companyName}
                </option>
              ))}
            </select>
          </div>

          <div className="bsr-filter-group">
            <label>Fiscal Year</label>
            <select
              name="fiscalYear"
              value={filters.fiscalYear}
              onChange={handleFilterChange}
            >
              {fiscalYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="bsr-filter-group">
            <label>As On Date</label>
            <input
              type="date"
              name="asOnDate"
              value={filters.asOnDate}
              onChange={handleFilterChange}
              max={todayStr}
            />
          </div>
        </div>
      </div>

      {/* Download Buttons */}
      {!loading && data && (
        <div className="bsr-header">
          <div className="bsr-pdf-button">
            <button onClick={generatePDF} className="bsr-fpdf-btn">
              Download PDF
            </button>
            <button onClick={generateExcel} className="bsr-fpdf-btn" style={{ marginLeft: '10px', background: '#28a745' }}>
              Download Excel
            </button>
          </div>
        </div>
      )}

      {loading && <div className="bsr-loading">Loading Trial Balance...</div>}

      {!loading && data && (
        <div className="bsr-container">
          {/* Company Info - EXACTLY like Balance Sheet */}
          <div className="bsr-company-header">
            {getLogoUrl() && getLogoUrl() !== "No Logo Available" && (
              <img 
                src={getLogoUrl()} 
                alt="Logo" 
                className="bsr-logo" 
                onError={handleLogoError}
              />
            )}
            <div className="bsr-company-text">
              <h2>{getCompanyName()}</h2>
              {getCompanyAddress() && (
                <p className="bsr-company-address">{getCompanyAddress()}</p>
              )}
            </div>
          </div>

          {/* Report Title - SIMILAR to Balance Sheet */}
          <div className="bsr-report-title">
            <h1>Trial Balance</h1>
            <p className="bsr-year">
              As on  date z{new Date(filters.asOnDate).toLocaleDateString('en-IN')}
            </p>
            <p className="bsr-company-code">
  Company Code: {filters.companyCode}
</p>

            <p className="bsr-company-code">Financial Year: {filters.fiscalYear}</p>
          </div>

          {/* Trial Balance Table */}
          <table className="bsr-table">
            <thead>
              <tr>
                <th>GL Code</th>
                <th>GL Description</th>
                <th>Debit (₹)</th>
                <th>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {data.trialBalanceSummary.rows.map(row => (
                <tr key={row.glCode}>
                  <td>{row.glCode}</td>
                  <td>{row.glDescription}</td>
                  <td className="bsr-amount">
                    {row.debit > 0 ? row.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : "-"}
                  </td>
                  <td className="bsr-amount">
                    {row.credit > 0 ? row.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals - STYLED like Balance Sheet */}
          <div className="bsr-totals">
            <div className="bsr-total-item">
              <span>Total Debit:</span>
              <span className="bsr-total-amount">
                ₹{data.trialBalanceSummary.totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="bsr-total-item">
              <span>Total Credit:</span>
              <span className="bsr-total-amount">
                ₹{data.trialBalanceSummary.totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="bsr-total-item" style={{ 
              borderTop: '2px solid #dee2e6', 
              marginTop: '10px', 
              paddingTop: '10px',
              fontWeight: 'bold',
              color: data.trialBalanceSummary.isBalanced ? '#28a745' : '#dc3545'
            }}>
              <span>Status:</span>
              <span>
                {data.trialBalanceSummary.isBalanced ? "✓ BALANCED" : "✗ NOT BALANCED"}
              </span>
            </div>
          </div>

          {/* Footer - EXACTLY like Balance Sheet */}
          <div className="bsr-footer">
            <p>Generated on: {new Date().toLocaleDateString()}</p>
            <p>Organisation: {user?.organisationId}</p>
          </div>
        </div>
      )}

      {!loading && !data && (
        <div className="bsr-no-data">
          <p>No Trial Balance data available for the selected filters.</p>
        </div>
      )}
    </div>
  );
};

export default TrialBalanceReport;